import {
  Alert,
  Box,
  ContentLayout,
  Header,
  Link,
  Pagination,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextFilter,
} from '@amzn/awsui-components-react';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { getRoutePathWithStudyPeriod, ROUTE_PATH } from '../../common/constants/route_paths';
import OrbitPageLayout from './components/OrbitPageLayout';
import { listComplianceSeasonsActions } from './redux/listComplianceSeasons';

const PAGE_SIZE = 10;
const DEFAULT_JURISDICTION = 'United States';
const IN_PROGRESS_STATES = ['CLOSE_REQUESTED', 'CLOSE_APPROVED', 'ARCHIVING'];

const seasonStatusIndicator = season => {
  const state = String(get(season, 'state', '')).toUpperCase();
  if (state === 'CLOSED') return <StatusIndicator type="success">Closed</StatusIndicator>;
  if (IN_PROGRESS_STATES.includes(state)) {
    return <StatusIndicator type="in-progress">Closing</StatusIndicator>;
  }
  if (state === 'CLOSE_FAILED') {
    return <StatusIndicator type="error">Close failed</StatusIndicator>;
  }
  return <StatusIndicator type="info">Open</StatusIndicator>;
};

const ComplianceSeasonPage = ({ seasons, isFetching, error, fetchSeasons, navigateTo }) => {
  const [filteringText, setFilteringText] = useState('');
  const [currentPageIndex, setCurrentPageIndex] = useState(1);

  useEffect(() => {
    fetchSeasons();
  }, [fetchSeasons]);

  const filteredSeasons = seasons.filter(item => {
    if (!filteringText) return true;
    const query = filteringText.toLowerCase();
    return (
      String(get(item, 'studyPeriod', '')).toLowerCase().includes(query) ||
      String(get(item, 'state', '')).toLowerCase().includes(query)
    );
  });

  const pagesCount = Math.max(1, Math.ceil(filteredSeasons.length / PAGE_SIZE));
  const startIndex = (currentPageIndex - 1) * PAGE_SIZE;
  const paginatedSeasons = filteredSeasons.slice(startIndex, startIndex + PAGE_SIZE);

  const content = (
    <ContentLayout
      headerVariant="high-contrast"
      maxContentWidth={1440}
      header={
        <div style={{ padding: '48px 0 24px' }}>
          <SpaceBetween size="xs">
            <Box fontSize="display-l" variant="h1" padding="n">
              <span style={{ color: '#ffffff' }}>
                <span style={{ fontWeight: 700 }}>Amazon Orbit</span>
                <br />
                <span className="orbit-hero-subtitle">
                  Streamline your R&amp;D tax credit study
                </span>
              </span>
            </Box>
            <Box variant="p" margin={{ top: 'xs' }}>
              <span style={{ color: '#ffffff', maxWidth: 800, display: 'block' }}>
                Orbit is Amazon&apos;s platform for managing R&amp;D tax credit compliance seasons —
                from importing employee and cost data, through collecting labor and substantiation
                surveys, to computing qualified research expenses and preparing filings.
              </span>
            </Box>
          </SpaceBetween>
        </div>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" header="Could not load compliance seasons">
            Refresh the page to try again.
          </Alert>
        )}
        <Table
          variant="container"
          header={
            <Header variant="h2" counter={`(${filteredSeasons.length})`}>
              Compliance seasons
            </Header>
          }
          loading={isFetching}
          loadingText="Loading compliance seasons"
          empty={
            <Box textAlign="center" color="inherit">
              No compliance seasons found
            </Box>
          }
          items={paginatedSeasons}
          columnDefinitions={[
            {
              id: 'studyPeriod',
              header: 'Compliance season',
              cell: item => {
                const studyPeriod = String(get(item, 'studyPeriod', '')).trim();
                if (!studyPeriod) return DEFAULT_JURISDICTION;
                const path = getRoutePathWithStudyPeriod(ROUTE_PATH.STUDY_SUMMARY, studyPeriod);
                return (
                  <Link
                    href={path}
                    onFollow={event => {
                      event.preventDefault();
                      navigateTo(path);
                    }}
                  >
                    {`${DEFAULT_JURISDICTION} - ${studyPeriod}`}
                  </Link>
                );
              },
            },
            {
              id: 'totalBCs',
              header: 'Business components',
              cell: item => get(item, 'totalBCs', 0),
            },
            {
              id: 'totalSurveys',
              header: 'Surveys',
              cell: item => get(item, 'totalSurveys', 0),
            },
            {
              id: 'state',
              header: 'Status',
              cell: item => seasonStatusIndicator(item),
            },
          ]}
          filter={
            <TextFilter
              filteringPlaceholder="Search season"
              filteringText={filteringText}
              onChange={({ detail }) => {
                setFilteringText(detail.filteringText);
                setCurrentPageIndex(1);
              }}
            />
          }
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              pagesCount={pagesCount}
              onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
            />
          }
        />
      </SpaceBetween>
    </ContentLayout>
  );

  return <OrbitPageLayout>{content}</OrbitPageLayout>;
};

ComplianceSeasonPage.propTypes = {
  seasons: PropTypes.arrayOf(PropTypes.object),
  isFetching: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  fetchSeasons: PropTypes.func.isRequired,
  navigateTo: PropTypes.func.isRequired,
};

ComplianceSeasonPage.defaultProps = {
  seasons: [],
  isFetching: false,
  error: null,
};

const mapStateToProps = state => ({
  seasons: get(state, 'entities.complianceSeasons.data.seasons', []),
  isFetching: get(state, 'entities.complianceSeasons.isFetching', false),
  error: get(state, 'entities.complianceSeasons.error', null),
});

const mapDispatchToProps = dispatch => ({
  fetchSeasons: () => dispatch(listComplianceSeasonsActions.BEGIN()),
  navigateTo: path => dispatch(push(path)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ComplianceSeasonPage);
